// Call Queue with state buckets: Overdue (top — most urgent) / Due Now / Due Today / Completed
// Plus lead-age color chips (green<30m, yellow<4h, red>4h). Respects an optional cityFilter prop.
import { useEffect, useState } from "react";
import { Phone, Clock, AlertCircle, CheckCircle2, Filter } from "lucide-react";
import { API, RED, NEON, AMBER, ago, leadAge, authHeader } from "./shared";

// Overdue first — overdue leads are the most urgent in the 30-minute promise model.
const BUCKETS = [
  { key: "overdue",   label: "Overdue",   sublabel: "Past target",   Icon: AlertCircle, accent: RED },
  { key: "due_now",   label: "Due Now",   sublabel: "≤ 1 hour",      Icon: Phone,       accent: AMBER },
  { key: "due_today", label: "Due Today", sublabel: "≤ 24 hours",    Icon: Clock,       accent: "#5C8DFF" },
  { key: "completed", label: "Completed", sublabel: "Contact logged",Icon: CheckCircle2, accent: NEON },
];

export function CallQueue({ token, onPickLead, cityFilter }) {
  const [buckets, setBuckets] = useState({ overdue: [], due_now: [], due_today: [], completed: [], counts: {} });
  const [active, setActive] = useState("overdue");

  useEffect(() => {
    let live = true;
    const url = cityFilter
      ? `${API}/call-queue/buckets?city=${encodeURIComponent(cityFilter)}`
      : `${API}/call-queue/buckets`;
    const load = () =>
      fetch(url, { headers: authHeader(token) })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (live && d) setBuckets(d); })
        .catch(() => {});
    load();
    const id = setInterval(load, 5000);
    return () => { live = false; clearInterval(id); };
  }, [token, cityFilter]);

  const rows = buckets[active] || [];

  return (
    <div className="rounded-sm border border-white/10 bg-black/50" data-testid="call-queue">
      {/* Header + bucket tabs */}
      <div className="px-5 py-3 border-b border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <p className="font-heavy text-[14px] inline-flex items-center gap-2">
          <Phone size={14} color={RED} /> CALL QUEUE — 30-MIN PROMISE
          {cityFilter && (
            <span className="ml-1 px-1.5 py-0.5 rounded-sm border text-[9.5px] font-extrabold tracking-[0.12em]"
              style={{ borderColor: RED, color: RED, background: "rgba(255,42,60,0.10)" }}
              data-testid="queue-city-filter">
              CITY: {cityFilter.toUpperCase()}
            </span>
          )}
        </p>
        <span className="text-[10px] tracking-[0.16em] font-bold text-white/45 inline-flex items-center gap-1.5">
          <Filter size={11} /> Click a lead → open detail
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-5 py-3 border-b border-white/5">
        {BUCKETS.map(({ key, label, sublabel, Icon, accent }) => {
          const count = buckets.counts?.[key] ?? 0;
          const isActive = active === key;
          return (
            <button
              key={key}
              onClick={() => setActive(key)}
              data-testid={`bucket-${key}`}
              className="text-left rounded-sm border px-3 py-2.5 transition-colors"
              style={{
                borderColor: isActive ? accent : "rgba(255,255,255,0.10)",
                background: isActive ? `${accent}14` : "rgba(0,0,0,0.30)",
              }}
            >
              <div className="flex items-center justify-between">
                <Icon size={12} style={{ color: accent }} />
                <span className="font-heavy text-[18px] leading-none" style={{ color: accent }}>{count}</span>
              </div>
              <p className="mt-1.5 text-[11px] font-extrabold tracking-tight text-white">{label}</p>
              <p className="text-[9.5px] tracking-[0.12em] font-bold text-white/45 uppercase">{sublabel}</p>
            </button>
          );
        })}
      </div>

      {/* Rows */}
      <div className="divide-y divide-white/5 max-h-[480px] overflow-y-auto">
        {rows.length === 0 && (
          <p className="px-5 py-10 text-center text-white/45 text-[13px]">
            Nothing in this bucket. {active === "due_now" ? "You're on top of the promise. 🟢" : ""}
          </p>
        )}
        {rows.map((l) => {
          const age = leadAge(l.created_at);
          return (
            <button
              key={l.id}
              onClick={() => onPickLead && onPickLead(l.id)}
              data-testid={`queue-row-${l.id.slice(0, 8)}`}
              className="w-full grid grid-cols-12 gap-3 px-5 py-3 hover:bg-white/[0.03] text-left"
            >
              <span
                className="col-span-2 sm:col-span-1 inline-flex items-center justify-center px-1.5 py-0.5 rounded-sm border text-[9.5px] font-extrabold tracking-[0.12em]"
                style={{ borderColor: age.color, color: age.color, background: `${age.color}10` }}
                title={`Lead age: ${age.text}`}
                data-testid={`lead-age-chip-queue-${l.id.slice(0, 8)}`}
              >
                {age.text}
              </span>
              <span className="col-span-10 sm:col-span-3 font-heavy text-[13px] text-white truncate">
                {[l.first_name, l.last_name].filter(Boolean).join(" ") || l.email}
              </span>
              <span className="col-span-6 sm:col-span-3 text-[11.5px] text-white/70 font-mono truncate">{l.phone}</span>
              <span className="hidden sm:inline col-span-2 text-[11px] text-white/65 truncate">{l.city || "—"}</span>
              <span className="col-span-6 sm:col-span-2 text-[11px] text-white/65 truncate">{l.interest || "—"}</span>
              <span className="col-span-12 sm:col-span-1 text-right text-[10.5px] text-white/45 font-mono">
                {ago(l.created_at)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
